import { ChangeEvent, MouseEvent, ReactNode } from 'react';
import {
  TableRow,
  TablePagination,
  TableCell,
  TableSortLabel,
  useMediaQuery,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { TablePaginationActions } from './paginationActions';
import {
  StyledTable,
  StyledTableBody,
  StyledTableCell,
  StyledTableFooter,
  StyledTableHead,
  StyledTableContainer,
  StyledBoxContainer,
} from './styles';
import { Text } from '@/components/atoms/text';
import { PushPinOutlined } from '@mui/icons-material';
import { Desktop } from '@/components/molecules/viewport/desktop';

export type Header = {
  id: string;
  label: string;
  sortable?: boolean;
  numeric?: boolean;
  minWidth?: number;
};

type TableProps = {
  customHeader?: {
    title: string;
    colSpan: number;
  };
  headers: Header[];
  children?: ReactNode;
  rowsLabel?: string;
  listTotal?: number;
  aria_label?: string;
  page?: number;
  pinnedColumn?: string;
  rowsPerPage?: number;
  showPagination?: boolean;
  onGetPage?: (page: number) => void;
  onGetRowsPage?: (rowsPerPage: number) => void;
  order?: 'asc' | 'desc';
  orderBy?: string;
  onRequestSort?: (property: string) => void;
  styles?: {
    marginBottom?: string | number;
  };
  customPagination?: ReactNode;
};

export const Table = ({
  customHeader,
  headers,
  children,
  rowsLabel,
  listTotal,
  pinnedColumn,
  aria_label,
  page = 0,
  rowsPerPage = 10,
  showPagination,
  onGetPage,
  onGetRowsPage,
  order = 'asc',
  orderBy = '',
  onRequestSort,
  styles,
  customPagination,
}: TableProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const handleChangePage = (_: MouseEvent<HTMLButtonElement> | null, newPage: number) => {
    onGetPage?.(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onGetRowsPage?.(parseInt(event.target.value, 10));
    onGetPage?.(0);
  };

  const createSortHandler = (property: string) => () => {
    onRequestSort?.(property);
  };

  return (
    <>
      <StyledTableContainer marginBottom={styles?.marginBottom}>
        <StyledTable aria-label={`${aria_label}-table`}>
          <StyledTableHead>
            {customHeader && (
              <TableRow>
                <TableCell
                  align="center"
                  colSpan={customHeader.colSpan}
                  sx={(theme) => ({
                    backgroundColor: theme.palette.primary.states.focusVisible,
                  })}
                >
                  <Text variant="subtitle1" color="text.primary">
                    {customHeader.title}
                  </Text>
                </TableCell>
              </TableRow>
            )}

            <TableRow>
              {headers.map(({ id, label, sortable = false, numeric, minWidth }, index) => (
                <StyledTableCell
                  key={index}
                  align={numeric ? 'right' : 'left'}
                  sx={{ minWidth: minWidth || 224 }}
                  sortDirection={orderBy === id ? order : false}
                  className={index === 0 ? 'sticky-col' : undefined}
                >
                  <Box
                    sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                  >
                    {sortable ? (
                      <TableSortLabel
                        active={orderBy === id}
                        direction={orderBy === id ? order : 'asc'}
                        onClick={createSortHandler(id)}
                        sx={{
                          '&.MuiTableSortLabel-root': {
                            '&.Mui-active': {
                              '& .MuiTableSortLabel-icon': {
                                opacity: 1,
                              },
                            },
                          },
                        }}
                      >
                        {label}
                      </TableSortLabel>
                    ) : (
                      label
                    )}
                    <Desktop>
                      {pinnedColumn === label && (
                        <PushPinOutlined
                          sx={{ fontSize: 18, color: theme.palette.action.active }}
                        />
                      )}
                    </Desktop>
                  </Box>
                </StyledTableCell>
              ))}
            </TableRow>
          </StyledTableHead>

          <StyledTableBody>{children}</StyledTableBody>
        </StyledTable>
      </StyledTableContainer>

      {showPagination && (
        <StyledBoxContainer>
          <StyledTableFooter>
            {customPagination}
            <TableRow>
              <TablePagination
                rowsPerPageOptions={isMobile ? [] : [10, 25, 50]}
                colSpan={headers.length}
                count={listTotal ?? 0}
                rowsPerPage={rowsPerPage}
                page={page}
                SelectProps={{
                  inputProps: {
                    'aria-label': rowsLabel,
                  },
                  native: true,
                }}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                ActionsComponent={TablePaginationActions}
                labelRowsPerPage={isMobile ? '' : rowsLabel}
                sx={{
                  border: 'none',
                }}
              />
            </TableRow>
          </StyledTableFooter>
        </StyledBoxContainer>
      )}
    </>
  );
};
